import { RegisterColumns } from '../types'
import type { ReportHeader, SalesRegisterTotal } from '../types'
import type { RegisterGroups } from '../report-groups'
import { excelPeriodLines, excelReportFileName } from './excel-workbook'
import type { ExcelColumn, ExcelReport, ExcelRow } from './excel-workbook'
import { visibleColumns } from './sales-register-columns'
import type { RegisterLine } from './sales-register-columns'

interface SalesRegisterSheetOptions {
  header: ReportHeader
  /** The outlets and channel the period was read for, as the filters name them. */
  scope: string
  /** The report's own name — "Sales Register By Customer". */
  title: string
  groups: RegisterGroups
  /** The period's total, as the table prints it under the blocks. */
  total: SalesRegisterTotal
  columns?: RegisterColumns
  noun?: string
  omit?: string[]
  notes?: string[]
}

/**
 * The register in blocks as an Excel sheet — the same blocks, columns and
 * totals as `SalesRegisterTable`, in the legacy "Sales Register By …" layout:
 * each block's name, its invoices, its subtotal, then the period total.
 *
 * Built here rather than by the server, whose register export is the flat
 * sheet: the blocks only exist on this side.
 */
export function salesRegisterSheet({
  header,
  scope,
  title,
  groups,
  total,
  columns = RegisterColumns.All,
  noun = 'invoice',
  omit = [],
  notes,
}: SalesRegisterSheetOptions): ExcelReport {
  const count = (value: number) => `${value} ${value === 1 ? noun : `${noun}s`}`
  const visible = visibleColumns(columns, omit).filter(
    (column) => !column.visibleWhen || column.visibleWhen(groups.rows),
  )

  const sheetColumns: ExcelColumn[] = visible.map((column) => ({
    header: column.header,
    type: column.id === 'at' ? 'datetime' : column.format === 'usd' ? 'money' : 'text',
    width: column.id === 'invoice' ? 20 : column.id === 'status' ? 10 : undefined,
    optional: column.hideWhenEmpty,
  }))

  // A cell's raw value: the instant, not the screen's formatted time, so Excel
  // holds a real date.
  const values = (row: RegisterLine) =>
    visible.map((column) => (column.id === 'at' ? row.at : column.value(row)))

  /** A sum row — its name where the date goes, its count where the invoice goes. */
  const sum = (kind: 'subtotal' | 'total', line: SalesRegisterTotal, label: string): ExcelRow => ({
    kind,
    values: visible.map((column, index) =>
      column.id === 'at'
        ? label
        : column.id === 'invoice'
          ? count(line.invoices)
          : values({ ...line, isTotal: true })[index],
    ),
  })

  const rows: ExcelRow[] = []

  groups.rows.forEach((row, index) => {
    const name = groups.nameOf(row)
    const previous = groups.rows[index - 1]
    const next = groups.rows[index + 1]

    if (!previous || groups.nameOf(previous) !== name) {
      rows.push({ kind: 'group', values: [name] })
    }

    rows.push(values(row))

    const subtotal = groups.subtotals.get(name)

    if (subtotal && (!next || groups.nameOf(next) !== name)) {
      rows.push(sum('subtotal', subtotal, `${name} total`))
    }
  })

  if (groups.rows.length) rows.push(sum('total', total, 'Period total'))

  return {
    fileName: excelReportFileName(title.replace(/\s+/g, '-'), header.from, header.to),
    sheetName: title,
    business: header.merchantName,
    title,
    lines: excelPeriodLines(header.from, header.to, scope),
    columns: sheetColumns,
    rows,
    notes,
    empty: 'No invoices in this period',
  }
}
